import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Image, Input, Button, message } from '../../ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex border-b border-line last:border-b-0">
      <div className="w-32 shrink-0 bg-surface-2 px-3 py-2 text-[12px] text-muted">{label}</div>
      <div className="flex-1 px-3 py-2 text-[13px] text-content">{children}</div>
    </div>
  );
}

/**
 * Approver's review of a submitted payment — Tailwind layout. Everything needed to verify against the
 * bank before approving: UTR/reference, proof image(s), and the order's paid/due context (so a duplicate
 * or over-payment is obvious). Approve posts the ledger credit; Reject needs a reason.
 */
export function PaymentReviewModal({ payment, open, onClose }: { payment: any | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['order-pay-summary', payment?.orderId],
    queryFn: () => request<any>({ url: `/orders/${payment.orderId}/payment-summary` }),
    enabled: open && !!payment?.orderId,
  });

  const invalidate = () => {
    ['payments', 'orders', 'retailer-ledger', 'badges'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    qc.invalidateQueries({ queryKey: ['order-payments', payment?.orderId] });
  };
  const approve = useMutation({
    mutationFn: () => request({ url: `/payments/${payment.id}/approve`, method: 'POST' }),
    onSuccess: () => { message.success('Payment approved — ledger updated'); invalidate(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to approve'),
  });
  const reject = useMutation({
    mutationFn: () => request({ url: `/payments/${payment.id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Payment rejected'); setRejecting(false); setReason(''); invalidate(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to reject'),
  });

  if (!payment) return null;
  const files: string[] = payment.attachmentUrls?.length ? payment.attachmentUrls : (payment.attachmentUrl ? [payment.attachmentUrl] : []);
  const submitterName = payment.submittedByName || payment.retailerName || '—';
  const isRetailerSubmitter = payment.submittedByRole === 'Retailer';
  const amt = Number(payment.amount);
  const due = Number(summary?.due ?? 0);
  const afterApprove = due - amt;

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) { setRejecting(false); onClose(); } }} width={780} title={`Review payment ${payment.paymentNo}`}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.25fr_1fr]">
          {/* Details */}
          <div className="overflow-hidden rounded-card border border-line">
            <Field label="Retailer">{payment.retailerName}{payment.retailerCode ? ` · ${payment.retailerCode}` : ''}</Field>
            <Field label="Order">
              {payment.orderNo ? (
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(`/orders/${payment.orderId}`); }}
                  className="inline-flex items-center rounded-pill bg-sky-500/10 px-2 py-0.5 text-[12px] font-medium text-sky-600 hover:bg-sky-500/20"
                >
                  {payment.orderNo}
                </button>
              ) : <span className="text-muted">On-account</span>}
            </Field>
            <Field label="Amount"><span className="text-[15px] font-bold">{inr2(payment.amount)}</span></Field>
            <Field label="Mode">{payment.mode}</Field>
            <Field label="Date">{payment.paymentDate}</Field>
            <Field label="Reference No">{payment.referenceNo || '—'}</Field>
            <Field label="UTR / Bank Ref"><span className="font-semibold">{payment.utr || '—'}</span></Field>
            <Field label="Submitted by">
              <span className="inline-flex items-center gap-2">
                {isRetailerSubmitter ? (
                  <button type="button" onClick={() => { onClose(); navigate(`/retailers/${payment.retailerId}`); }} className="text-content hover:text-brand-700 hover:underline">
                    {submitterName}
                  </button>
                ) : <span>{submitterName}</span>}
                {payment.submittedByRole && (
                  <span className="inline-flex items-center rounded-pill bg-brand-600/10 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:text-brand-300">{payment.submittedByRole}</span>
                )}
              </span>
            </Field>
          </div>

          {/* Proof */}
          <div>
            <div className="mb-2 text-[13px] font-semibold text-content">Payment proof</div>
            {files.length ? (
              <Image.PreviewGroup>
                <div className="flex flex-wrap gap-2">
                  {files.map((u, i) => (/\.pdf($|\?)/i.test(u)
                    ? <a key={i} href={u} target="_blank" rel="noopener" className="rounded-card border border-line px-3 py-2 text-[13px] text-brand-700 hover:bg-surface-2">Proof {i + 1} (PDF)</a>
                    : <Image key={i} src={u} width={132} height={132} className="rounded-card object-cover" />))}
                </div>
              </Image.PreviewGroup>
            ) : (
              <div className="rounded-card border border-warning-500/20 bg-warning-500/10 px-3 py-2 text-[13px] text-warning-600">No proof attached.</div>
            )}
          </div>
        </div>

        {/* Order context */}
        {payment.orderId && summary && (
          <div className={`rounded-card border px-3 py-2.5 text-[13px] ${afterApprove < -0.01 ? 'border-danger-500/20 bg-danger-500/10 text-danger-600' : 'border-sky-500/20 bg-sky-500/10 text-sky-600'}`}>
            <div className="font-semibold">Order total {inr2(summary.grandTotal)} · already paid {inr2(summary.paid)}</div>
            <div className="mt-0.5">
              {afterApprove < -0.01
                ? `This ${inr2(amt)} exceeds the ${inr2(due)} still due — approving would overpay. Check for a duplicate.`
                : afterApprove <= 0.01
                  ? 'Approving clears this order — remaining due becomes ₹0.'
                  : `After approval, ${inr2(afterApprove)} will still be due on the order.`}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
          {rejecting ? (
            <div className="flex w-full flex-col gap-2">
              <Input.TextArea rows={2} maxLength={255} placeholder="Reason for rejection (the submitter will see this — e.g. proof unclear, UTR mismatch)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="default" onClick={() => setRejecting(false)}>Cancel</Button>
                <Button variant="danger" icon={<XCircle size={16} />} loading={reject.isPending} disabled={!reason.trim()} onClick={() => reject.mutate()}>Confirm reject</Button>
              </div>
            </div>
          ) : (
            <>
              <Button variant="danger" icon={<XCircle size={16} />} onClick={() => setRejecting(true)}>Reject</Button>
              <Button variant="primary" icon={<CheckCircle2 size={16} />} loading={approve.isPending} onClick={() => approve.mutate()}>Approve &amp; post to ledger</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
